import type { ReviewContextDto } from '../../lib/api/endpoints';
import { formatCurrency } from '../../lib/format/currency';

export function ReviewHeader({ context }: { context: ReviewContextDto }) {
  const pnl = Number(context.lifecycle.netRealisedPnl);
  const pnlTone = pnl < 0 ? 'text-danger' : pnl > 0 ? 'text-success' : 'text-text';
  return (
    <div className="space-y-5 border-b border-border pb-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-normal">
            {context.lifecycle.symbol} · {context.lifecycle.status}
            {context.lifecycle.holdingPeriodDays !== null
              ? `, ${context.lifecycle.holdingPeriodDays} days`
              : ''}
          </h1>
          <p className="text-sm text-text-muted">
            {formatDate(context.lifecycle.firstEntryAt)} {'->'}{' '}
            {context.lifecycle.lastExitAt ? formatDate(context.lifecycle.lastExitAt) : 'Open'}
          </p>
        </div>
        <div className="text-right">
          <p className="text-xs uppercase tracking-normal text-text-muted">Net realised P&L</p>
          <div className={`font-mono text-xl font-semibold tabular-nums ${pnlTone}`}>
            {formatCurrency(pnl, 'USD')}
          </div>
        </div>
      </div>
      <LifecycleContext context={context} />
    </div>
  );
}

function LifecycleContext({ context }: { context: ReviewContextDto }) {
  return (
    <div className="space-y-4">
      <section className="space-y-2">
        <h2 className="text-sm font-semibold">Entries</h2>
        <LifecycleTable
          headers={['Date', 'Qty', 'Price', 'Gross', 'Running shares']}
          rows={context.lifecycle.entries.map((entry) => [
            formatDate(entry.date),
            formatNumber(entry.qty),
            formatCurrency(entry.price, 'USD'),
            formatCurrency(entry.grossAmount, 'USD'),
            formatNumber(entry.runningTotalShares),
          ])}
          footer={[
            'Total',
            formatNumber(context.lifecycle.totalSharesBought),
            formatCurrency(context.lifecycle.avgEntryPrice, 'USD'),
            formatCurrency(context.lifecycle.totalInvested, 'USD'),
            '',
          ]}
        />
      </section>

      <section className="space-y-2">
        <h2 className="text-sm font-semibold">Exits</h2>
        <LifecycleTable
          headers={['Date', 'Qty', 'Price', 'Gross', 'Closed']}
          rows={context.lifecycle.exits.map((exit) => [
            formatDate(exit.date),
            formatNumber(exit.qty),
            formatCurrency(exit.price, 'USD'),
            formatCurrency(exit.grossAmount, 'USD'),
            `${formatNumber(exit.pctOfPositionClosed)}%`,
          ])}
          footer={[
            'Total',
            formatNumber(context.lifecycle.totalSharesSold),
            formatCurrency(context.lifecycle.avgExitPrice, 'USD'),
            formatCurrency(context.lifecycle.totalExited, 'USD'),
            '',
          ]}
        />
      </section>

      <section className="grid gap-2 rounded border border-border bg-surface-raised p-3 text-sm sm:grid-cols-2">
        <Metric
          label="Gross realised P&L"
          value={formatCurrency(context.lifecycle.realisedPnl, 'USD')}
        />
        <Metric
          label="Total commissions"
          value={formatCurrency(context.lifecycle.totalCommissions, 'USD')}
        />
        <Metric
          label="Net realised P&L"
          value={formatCurrency(context.lifecycle.netRealisedPnl, 'USD')}
        />
        <Metric label="FX impact" value={formatCurrency(context.lifecycle.fxImpact, 'USD')} />
      </section>
    </div>
  );
}

function LifecycleTable({
  headers,
  rows,
  footer,
}: {
  headers: string[];
  rows: string[][];
  footer: string[];
}) {
  return (
    <div className="overflow-x-auto rounded border border-border">
      <table className="min-w-full divide-y divide-border text-sm">
        <thead className="bg-surface-raised text-left text-xs uppercase tracking-normal text-text-muted">
          <tr>
            {headers.map((header) => (
              <th key={header} className="px-3 py-2 font-medium">
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {rows.map((row) => (
            <tr key={row.join(':')}>
              {row.map((cell, index) => (
                <td key={`${cell}-${index}`} className="px-3 py-2 tabular-nums">
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
        <tfoot className="bg-surface-raised font-medium">
          <tr>
            {footer.map((cell, index) => (
              <td key={`${cell}-${index}`} className="px-3 py-2 tabular-nums">
                {cell}
              </td>
            ))}
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-text-muted">{label}</p>
      <p className="font-mono font-semibold tabular-nums">{value}</p>
    </div>
  );
}

function formatDate(value: string): string {
  return new Date(value).toLocaleDateString();
}

function formatNumber(value: string): string {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return value;
  return numeric.toLocaleString(undefined, { maximumFractionDigits: 6 });
}
